import React from 'react';
import {
  Bars3BottomLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { MAX_KEYWORDS, MAX_SOURCES } from '../../lib/sources';

/**
 * Step 5 (review) for a single article: summary of the chosen settings.
 * Extracted verbatim from pages/Articles/CreateArticle.js.
 */
export default function ReviewSummary({
  contentType,
  includeImages,
  includeImagesLabel,
  keywords,
  selectedComplexityLabel,
  selectedLanguageLabel,
  selectedStructureLabel,
  selectedToneLabel,
  sources,
  thumbnailSource,
}) {
  const { t } = useTranslation();

  return (
    <section className="bg-white py-2 space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <div className="flex items-start gap-3">
            <DocumentTextIcon className="h-5 w-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('articles.create.articleStructure.title')}</p>
              <p className="text-sm font-medium text-gray-900">{selectedStructureLabel}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <div className="flex items-start gap-3">
            <SparklesIcon className="h-5 w-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('articles.create.advancedSetup.toneOfVoice')}</p>
              <p className="text-sm font-medium text-gray-900">{selectedToneLabel}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <div className="flex items-start gap-3">
            <Bars3BottomLeftIcon className="h-5 w-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('articles.create.advancedSetup.complexity')}</p>
              <p className="text-sm font-medium text-gray-900">{selectedComplexityLabel}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <div className="flex items-start gap-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('articles.create.advancedSetup.language')}</p>
              <p className="text-sm font-medium text-gray-900">{selectedLanguageLabel}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <div className="flex items-start gap-3">
            <PhotoIcon className="h-5 w-5 text-gray-500 mt-0.5" />
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('articles.create.imageSettings.includeImages')}</p>
              <p className="text-sm font-medium text-gray-900">{includeImagesLabel}</p>
            </div>
          </div>
        </div>
        {includeImages && (
          <div className="rounded-xl border border-gray-200 p-4 bg-white">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-gray-500 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('articles.create.imageSettings.sources.stock.title')}</p>
                <p className="text-sm font-medium text-gray-900">{thumbnailSource === 'gemini' ? t('articles.create.imageSettings.sources.ai.title') : t('articles.create.imageSettings.sources.stock.title')}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* A changelog skips the keywords and sources steps, so there is nothing to review there. */}
      {contentType !== 'changelog' && (
      <>
      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          {t('articles.create.keywordForm.selectionTitle', { selected: keywords.length, max: MAX_KEYWORDS })}
        </p>
        {keywords.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {keywords.map((keyword) => (
              <span key={keyword} className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                {keyword}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t('articles.create.keywordForm.table.emptyTitle')}</p>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 p-4 bg-white">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          {t('articles.create.articleSources.selectionTitle', { selected: sources.length, max: MAX_SOURCES })}
        </p>
        {sources.length > 0 ? (
          <ul className="space-y-2">
            {sources.map((source) => (
              <li key={source.id || source.url || source.title} className="text-sm text-gray-900">
                {source.title || source.id}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">{t('articles.create.articleSources.emptyReview')}</p>
        )}
      </div>
      </>
      )}
    </section>
  );
}
